import { Component, Output, EventEmitter, OnInit, Input, ChangeDetectionStrategy, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatLegacyMenuModule } from '@angular/material/legacy-menu';
import { LabeledMenuComponent, MenuItem } from '@mode/shared/ui';
import { FrequencyType, SchedulesFacade } from '@mode/shared/schedules/contract';
import { Observable, Subject, combineLatest } from 'rxjs';
import { filter, first, map, takeUntil } from 'rxjs/operators';
import { FeatureFlag, FeatureFlagsFacade } from '@mode/shared/contract-common';
import { AccountFacade } from '@mode/accounts/contract';

interface cadenceMenuItem extends MenuItem {
  value: FrequencyType;
}

@Component({
  standalone: true,
  imports: [CommonModule, MatLegacyMenuModule, LabeledMenuComponent],
  selector: 'mode-cadence-selector',
  templateUrl: './cadence-selector.component.html',
  styleUrls: ['./cadence-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CadenceSelectorComponent implements OnInit {
  @Input() cadence: FrequencyType = 'daily';
  @Output() cadenceChange = new EventEmitter<string>();

  public availableIntervals$!: Observable<string[]>;
  public allowedOptions$!: Observable<cadenceMenuItem[]>;
  protected destroyed$ = new Subject<void>();
  public orgName!: string;

  orgContext$ = this._accountFacade.currentOrg$;

  public options: cadenceMenuItem[] = [
    {
      name: '15 minutes',
      value: 'every_15_minutes',
    },
    {
      name: '30 minutes',
      value: 'every_30_minutes',
    },
    {
      name: 'Hour',
      value: 'hourly',
      divider: true,
    },
    {
      name: 'Day',
      value: 'daily',
      desc: 'Monday - Sunday',
    },
    {
      name: 'Weekday',
      value: 'weekday',
      desc: 'Monday - Friday',
    },
    {
      name: 'Week',
      value: 'weekly',
      desc: 'Mondays',
    },
    {
      name: 'Month',
      value: 'monthly',
      desc: `1st of the month`,
    },
  ];

  public selected: MenuItem | null = null;
  public adminManagedScheduleIntervalsEnabled$: Observable<boolean>;
  public filteredOptions: cadenceMenuItem[] = [];

  constructor(
    private schedulesFacade: SchedulesFacade,
    private featureFlagsFacade: FeatureFlagsFacade,
    private _accountFacade: AccountFacade
  ) {
    this.adminManagedScheduleIntervalsEnabled$ = this.featureFlagsFacade.asObservable(
      FeatureFlag.AdminManagedScheduleIntervals
    );

    this.availableIntervals$ = this.schedulesFacade.availableIntervals$;

    this.allowedOptions$ = combineLatest([this.availableIntervals$, this.adminManagedScheduleIntervalsEnabled$]).pipe(
      map(([availableIntervals, flagEnabled]) => {
        return flagEnabled ? this.options.filter((option) => availableIntervals.includes(option.value)) : this.options;
      }),
      takeUntil(this.destroyed$)
    );
  }

  ngOnInit(): void {
    this.orgContext$
      .pipe(
        filter((org) => !!org?.username),
        first(),
        map((org) => {
          this.orgName = org.username;
          this.schedulesFacade.loadScheduleIntervalThreshold(this.orgName);
        })
      )
      .subscribe();

    this.allowedOptions$.subscribe((filteredOptions) => {
      this.filteredOptions = filteredOptions;

      const selectedOption = filteredOptions.find((option) => option.value === this.cadence);

      if (selectedOption) {
        this.setSelected(this.cadence, filteredOptions);
      } else if (filteredOptions.length > 0) {
        const fallbackOption = filteredOptions[0].value;
        this.setSelected(fallbackOption, filteredOptions);
        this.cadenceChange.emit(fallbackOption);
      }
    });
  }

  setSelected(filter: FrequencyType, options: cadenceMenuItem[]) {
    this.selected = options.find((option) => option.value === filter) || null;
  }

  onCadenceChange(option: cadenceMenuItem) {
    this.setSelected(option.value, this.filteredOptions);
    this.cadenceChange.emit(option.value);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}
